import React, { useEffect, useRef } from 'react';

import { useParams } from 'react-router-dom';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import { useDispatch, useSelector } from 'react-redux';
import { getDomainSlice, RootState } from 'store';
import Snackbars from 'components/Alerts';

import { DomainItem } from 'models/Domain';
import Typography from 'components/Typography';
import { camelToPascalCase, mergeErrors } from 'utils';
import {
  CrudViewProps,
  formSubmitHandler,
  getInitialFieldValues,
  renderErrorOrLoading
} from '../utils';
import useStyles from '../styles';
import {
  FormValues,
  yupSchemaToValidationFunc,
  Form,
  Buttons
} from '../../Forms';

const EditView = ({
  domain,
  fields,
  initialValues,
  title = `Edit ${domain.type}`,
  validationSchema,
  presubmitCallback,
  processDomainData,
  loading,
  error: externalError
}: CrudViewProps) => {
  const classes = useStyles();
  const resetButtonState = useRef<boolean | NodeJS.Timeout>(false);
  const { id: domainItemId } = useParams<{ id: string }>();
  const dispatch = useDispatch();

  const { actions, selector } = getDomainSlice(domain);

  const { isLoading, error: domainError } = useSelector(selector);
  const lastFetchedId = useRef<string | null>(null);

  useEffect(() => {
    if (
      actions.get.read &&
      domainItemId &&
      lastFetchedId.current !== domainItemId &&
      !isLoading
    ) {
      dispatch(actions.get.read(domainItemId));
      lastFetchedId.current = domainItemId;
    }
  }, [domainItemId, actions.get.read, isLoading]);

  // Clear button state timeout on unmount
  useEffect(() => {
    return () => {
      clearTimeout(resetButtonState.current as NodeJS.Timeout);
    };
  }, []);

  const data = useSelector<RootState, DomainItem | undefined>(state =>
    selector(state).items.find((item: DomainItem) => item.id === domainItemId)
  );

  if (fields == null) {
    return null;
  }

  const validationFunc = yupSchemaToValidationFunc(validationSchema);
  const apiCallback = (formValues: FormValues) =>
    dispatch(actions.edit(formValues, domainItemId));

  const initialFieldValues: FormValues =
    initialValues || getInitialFieldValues(fields, data, processDomainData);

  const error = mergeErrors([externalError ?? null, domainError ?? null]);

  const isInitialLoad = !data || loading;

  return (
    <>
      <Grid container className={classes.formContainer}>
        <Grid item xs={12}>
          <Typography variant="h1" data-qa-id="createDomainPageTitle">
            {title}
          </Typography>
          {error || isInitialLoad ? (
            renderErrorOrLoading(
              isInitialLoad === true,
              `edit${camelToPascalCase(domain.type)}Spinner`,
              error,
              classes
            )
          ) : (
            <Paper className={`${classes.root} ${classes.paper}`}>
              <Form
                validationFunc={validationFunc}
                fields={fields}
                onSubmit={formSubmitHandler(apiCallback, presubmitCallback)}
                initialValues={initialFieldValues}
                autoReset={false}
              >
                {({ submitting, submitSucceeded, submitErrors }) => (
                  <Buttons
                    isSubmitting={submitting}
                    success={!submitErrors && submitSucceeded}
                  />
                )}
              </Form>
            </Paper>
          )}
        </Grid>
      </Grid>
      <Snackbars />
    </>
  );
};

export default EditView;
